import type { BlockerStatus, TaskPriority, TaskStatus, TaskSummary } from '@mfos/shared';
import { Badge } from '../../components/Badge';
import { formatDateTime } from '../../lib/format';
import { phrase, useI18n } from '../../lib/i18n/runtime';
import type { MessageKey } from '../../lib/i18n/messages';
import { BLOCKER_STATUS_DISPLAY, TASK_PRIORITY_DISPLAY, TASK_STATUS_DISPLAY } from './task-display';

const STATUS_KEYS: Readonly<Record<TaskStatus, MessageKey>> = {
  BACKLOG: 'statusBacklog',
  PLANNED: 'statusPlanned',
  IN_PROGRESS: 'statusInProgress',
  BLOCKED: 'statusBlocked',
  IN_REVIEW: 'statusInReview',
  DONE: 'statusDone',
  ARCHIVED: 'statusArchived',
  CANCELLED: 'statusCancelled',
};

const PRIORITY_KEYS: Readonly<Record<TaskPriority, MessageKey>> = {
  LOW: 'priorityLowFull',
  NORMAL: 'priorityNormalFull',
  HIGH: 'priorityHighFull',
  CRITICAL: 'priorityCriticalFull',
};

export function TaskStatusBadge({ status }: { status: TaskStatus }) {
  const { t } = useI18n();
  const display = TASK_STATUS_DISPLAY[status];
  return <Badge tone={display.tone} label={t(STATUS_KEYS[status])} />;
}

export function TaskPriorityBadge({ priority }: { priority: TaskPriority }) {
  const { t } = useI18n();
  const display = TASK_PRIORITY_DISPLAY[priority];
  return <Badge tone={display.tone} label={t(PRIORITY_KEYS[priority])} />;
}

export function BlockerStatusBadge({ status }: { status: BlockerStatus }) {
  const display = BLOCKER_STATUS_DISPLAY[status];
  return <Badge tone={display.tone} label={display.label} />;
}

/** Due date in the user's zone; "Overdue" comes from the server-computed flag, in words. */
export function TaskDue({ task, timeZone }: { task: TaskSummary; timeZone: string | undefined }) {
  if (task.dueAt === null) {
    return <span>{task.noDeadline ? phrase('No deadline') : phrase('Deadline not set')}</span>;
  }
  return (
    <span>
      <time dateTime={task.dueAt}>{formatDateTime(task.dueAt, timeZone)}</time>
      {task.overdue && (
        <>
          {' '}
          <Badge tone="down" label={phrase('Overdue')} />
        </>
      )}
    </span>
  );
}
